import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { blogPostPathIn } from '../utils/blogLocale';

/* Translations used to ship as one block: all 19 namespaces in both
   languages (~144 KB) inside the main bundle, downloaded and parsed on
   every first visit before anything could paint.

   Now only what the shell and the homepage render ships up front, in
   English — `/` is English and it is where most visits land. Everything
   else (every other namespace, and all of Spanish) is its own small chunk,
   fetched for the language the visitor is reading and the page they are
   on. A new `locales/{en,es}/<ns>.json` needs no import here: the glob
   picks it up. */
import commonEN from './locales/en/common.json';
import studioEN from './locales/en/studio.json';
import projectsEN from './locales/en/projects.json';

// Header, footer and homepage (WorkCard reads `projects`).
const CORE_NAMESPACES = ['common', 'studio', 'projects'];

const lazyLocales = import.meta.glob(
  [
    './locales/*/*.json',
    '!./locales/en/common.json',
    '!./locales/en/studio.json',
    '!./locales/en/projects.json',
  ],
  { import: 'default' }
);

const lazyLocaleBackend = {
  type: 'backend',
  init() {},
  read(language, namespace, callback) {
    const load = lazyLocales[`./locales/${language}/${namespace}.json`];
    if (!load) {
      callback(new Error(`No ${language}/${namespace} translations`), false);
      return;
    }
    load().then((data) => callback(null, data), (error) => callback(error, false));
  },
};

// Check if path is a Spanish locale path (exactly /es or starts with /es/)
const isSpanishPath = (path) => {
  return path === '/es' || path.startsWith('/es/');
};

// Get language from URL path
const getLanguageFromPath = () => {
  if (typeof window !== 'undefined') {
    const path = window.location.pathname;
    if (isSpanishPath(path)) {
      return 'es';
    }
  }
  return 'en';
};

/* Resolves once the current language's core namespaces are in the store.
   Immediate for English; one round trip for Spanish. index.jsx waits on
   it before the first render so no component paints a raw key. */
export const i18nReady = i18n
  .use(lazyLocaleBackend)
  .use(initReactI18next)
  .init({
    resources: {
      en: { common: commonEN, studio: studioEN, projects: projectsEN },
    },
    // The bundled resources above are partial; the backend fills the rest.
    partialBundledLanguages: true,
    lng: getLanguageFromPath(),
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: CORE_NAMESPACES,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    react: {
      useSuspense: false,
    },
  });

/* For lazy routes: resolves when `namespaces` are loaded for the current
   language. It never rejects — a failed fetch must not take the route
   down with it; react-i18next retries the namespace when it renders. */
export const loadNamespaces = (namespaces) =>
  namespaces.length
    ? i18nReady.then(() => i18n.loadNamespaces(namespaces)).catch(() => {})
    : Promise.resolve();

export default i18n;

// Helper to change language and navigate
export const changeLanguage = (lang, currentPath, navigate) => {
  i18n.changeLanguage(lang);

  // A blog post's translation has a different slug, not just a prefix.
  const blogPath = blogPostPathIn(currentPath, lang);
  if (blogPath) {
    navigate(blogPath);
    return;
  }

  // Check if path is Spanish
  const isCurrentlySpanish = currentPath === '/es' || currentPath.startsWith('/es/');

  // Calculate new path
  let newPath;
  if (lang === 'es') {
    // Going to Spanish - add /es prefix
    if (isCurrentlySpanish) {
      newPath = currentPath; // Already Spanish
    } else {
      newPath = `/es${currentPath === '/' ? '' : currentPath}`;
    }
  } else {
    // Going to English - remove /es prefix
    if (isCurrentlySpanish) {
      newPath = currentPath.replace(/^\/es/, '') || '/';
    } else {
      newPath = currentPath; // Already English
    }
  }

  navigate(newPath);
};

// Get current language from path
export const getLanguageFromURL = (pathname) => {
  return (pathname === '/es' || pathname.startsWith('/es/')) ? 'es' : 'en';
};

// Remove language prefix from path for route matching
export const removeLanguagePrefix = (pathname) => {
  if (pathname === '/es' || pathname.startsWith('/es/')) {
    const newPath = pathname.replace(/^\/es/, '');
    return newPath || '/';
  }
  return pathname;
};
